/**
 * Story `be-fe-menu-aan-uit` — welke apps en menu-items System voor deze gebruiker heeft uitgezet.
 *
 * **De enige lezer van whoami `menuHidden`** (scan `menuVisibility.scan.test.ts`). De backend levert de uitgezette
 * sleutels al opgelost (rol-voorrang, System = leeg); de FE rekent niets zelf uit. Ontbreekt het veld (oudere
 * backend) of is het geen lijst, dan staat alles aan.
 *
 * De sleutel is afgeleid uit `apps.config.ts` — geen code per app: `menu.<appId>` en `menu.<appId>.<tabId>`, in
 * kleine letters (de backend eist `^menu\.[a-z0-9-]+(\.[a-z0-9-]+)?$`).
 *
 * Tonen ≠ mogen: dit verbergt alleen (menu, tab-strip, route-guard). Er is geen backend-afdwinging per app; die
 * bestaat alleen voor de verzekeraar-gebonden hersteller (EPIC-019 K1).
 *
 * Puur en `unknown`-tolerant; geen React-import.
 */
import type { App, Tab } from '@/config/apps.config';

import { isAppHiddenByInvoicing } from './invoicingActive';

/** Wat een app-check nodig heeft: de id (menu-sleutel) en de declaratieve verberg-vlaggen. */
type AppRef = Pick<App, 'id'> & Partial<Pick<App, 'hiddenWhenInvoicingInactive'>>;

/** De sleutel van een app (`menu.inkoop`) of van één tab daarin (`menu.inkoop.bestellijst`). */
export function menuKey(appId: string, tabId?: string): string {
    const app = appId.toLowerCase();
    return tabId === undefined ? `menu.${app}` : `menu.${app}.${tabId.toLowerCase()}`;
}

/** De uitgezette sleutels van deze gebruiker (lege set bij een ontbrekend of ongeldig veld). */
export function menuHiddenKeys(user: unknown): ReadonlySet<string> {
    if (user === null || typeof user !== 'object') return EMPTY;
    const raw = (user as { menuHidden?: unknown }).menuHidden;
    if (!Array.isArray(raw)) return EMPTY;
    return new Set(raw.filter((k): k is string => typeof k === 'string'));
}

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Staat deze app voor de gebruiker uit (AC 4: dan ook al zijn tabs)? Twee redenen, hetzelfde gevolg:
 * uitgezet in Systeem → Menu, of (story `be-fe-verzekeraar-alleen-lezen-en-facturatie-verbergen`, AC 7) een app met
 * `hiddenWhenInvoicingInactive` terwijl facturatie bij de hersteller uit staat. Zo volgen app-wisselaar, tab-strip,
 * route-guard en `appEntryRoute` beide redenen via één classificatie.
 */
export function isAppHiddenByMenu(app: AppRef, user: unknown): boolean {
    return menuHiddenKeys(user).has(menuKey(app.id)) || isAppHiddenByInvoicing(app, user);
}

/** Staat deze tab voor de gebruiker uit — zelf, of omdat zijn app uit staat? */
export function isTabHiddenByMenu(app: AppRef, tab: Pick<Tab, 'id'>, user: unknown): boolean {
    if (isAppHiddenByMenu(app, user)) return true;
    return menuHiddenKeys(user).has(menuKey(app.id, tab.id));
}
